"use client";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, dayjs, formatTitleForUrl } from "utils";
import { ShellLink } from "@/src/components";
import { BaseFoldableItem } from "./BasefoldableItem";
import { PriceChangePer } from "./PriceChangePer";
import Translations from "@translate/components/CoPilot/CopilotAgent/TimeLineItem/OtherAlertItem";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";
import { ROUTER_PATH } from "@/src/config";

const I18n = I18nUtil.createScoped(Translations);

interface OtherAlertItemProps {
  title: string;
  id: string;
  category: string;
  summary: string;
  type?: string;
  level: "High" | "Medium" | "Low";
  created: number;
  impact?: string;
  agentId?: string;
  sourceLink?: string;
  assets?: any[];
  onChartClick?: (value: {
    question?: string;
    title?: string;
    desc?: string;
    id?: string;
    sourceLink?: string;
    type?: string;
  }) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const OtherAlertItem = ({
  id,
  title,
  summary,
  impact,
  category,
  level,
  created,
  sourceLink,
  onChartClick,
  isOpen = false,
  onOpenChange,
  agentId,
  assets,
}: OtherAlertItemProps) => {
  const [isPc] = useIsPc();
  const typeClassNames = {
    CRYPTO: "text-content_brand_primary",
    STOCK: "text-content_accent4_primary",
  };

  const categoryMapping = {
    STOCK: "Stock",
    CRYPTO: "Crypto",
  };

  const levelClassNames = {
    High: "bg-content_accent3_primary",
    Medium: "bg-content_accent5_primary",
    Low: "bg-content_quaternary",
  };

  const LeftElement = () => {
    return (
      <div className="flex md:items-center md:gap-[10px] gap-1 h-full md:flex-row flex-col">
        <span
          className={cn(
            "body_bold_s",
            typeClassNames[category as keyof typeof typeClassNames],
          )}
        >
          {categoryMapping[category as keyof typeof categoryMapping] || ""}
        </span>
        <div className="flex items-center gap-[10px]">
          <div className="flex items-center gap-1">
            <span
              className={cn(
                "w-[6px] h-[6px] rounded-full",
                levelClassNames[level as keyof typeof levelClassNames],
              )}
            />
            <span className="body_medium_s text-content_secondary">
              {level}
            </span>
          </div>
          <span className="body_s text-content_tertiary">
            {dayjs(created).format("HH:mm")}
          </span>
        </div>
      </div>
    );
  };
  return (
    <BaseFoldableItem
      leftElement={<LeftElement />}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      containerClassName="md:gap-2"
      onChartClick={() =>
        onChartClick?.({
          title: title,
          desc: summary,
          id: id,
          sourceLink: sourceLink ?? "",
          question: I18n.t("Give me more info about this news"),
          type: "ALERT",
        })
      }
    >
      <div className="flex flex-col gap-2">
        <div
          className={cn(
            "title_l text-content_primary",
            !isOpen && "line-clamp-2 h-6 md:h-fit",
          )}
        >
          {title}
        </div>
        <div>
          {impact && (
            <div
              className={cn(
                "body_s text-content_secondary border-l-2 border-border_accent4_solid pl-3 transition-opacity duration-200",
                !isOpen && "line-clamp-2",
              )}
            >
              {impact}
            </div>
          )}
        </div>
        {isOpen && (
          <div
            className={cn(
              "body_output_m text-content_primary transition-opacity duration-200",
            )}
          >
            {summary}
          </div>
        )}
        <div className="flex justify-between items-center mt-1">
          {assets && <PriceChangePer assets={assets} />}
          {isOpen && sourceLink && agentId !== "8" && (
            <ShellLink
              aria-label="Other Alert Item"
              data-openreplay-label={`Other Alert Item Link | ${`${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(sourceLink || "")}`}`}
              id="lnk-other-alert-item"
              onClick={(e) => {
                e.stopPropagation();
              }}
              target={isPc ? "_blank" : "_self"}
              href={`${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(sourceLink || "")}`}
              className="flex items-center px-2 py-1 rounded-[20px] bg-background_white_overlay5 w-fit cursor-pointer"
            >
              <span className="body_m text-content_secondary">
                {agentId === "4" ? I18n.t("View Summary") : I18n.t("Read News")}
              </span>
              <Icon
                name="chevron-right"
                size={16}
                color={ospThemes.colors.content_secondary}
              />
            </ShellLink>
          )}
        </div>
      </div>
    </BaseFoldableItem>
  );
};
